import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import ChapterHeader from "@/components/landing/ChapterHeader";
import { IMAGES, SEMIFREDDI_MENU } from "@/lib/constants";
import { useLang } from "@/lib/i18n";

export default function Semifreddi() {
  const { t } = useLang();
  const [selected, setSelected] = useState(null);

  return (
    <section id="semifreddi" data-testid="semifreddi-section" className="grain relative overflow-hidden py-28 text-ivory sm:py-36">
      <div className="texture-tiles absolute inset-0" aria-hidden="true" />
      <div className="absolute inset-0 bg-night/90" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <ChapterHeader number="03" title={t.semifreddi.title} subtitle={t.semifreddi.subtitle} dark />
        <div className="grid items-center gap-16 lg:grid-cols-12">
          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: -2 }}
              whileInView={{ opacity: 1, y: 0, rotate: -2 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="arch-frame aspect-[3/4] w-3/4 overflow-hidden border border-ivory/15"
            >
              <img src={IMAGES.semifreddo1} alt="Torta gelato decorata con treno di cioccolato" loading="lazy" className="h-full w-full object-cover" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 40, rotate: 3 }}
              whileInView={{ opacity: 1, y: 0, rotate: 3 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="arch-frame absolute -bottom-10 right-0 aspect-[3/4] w-1/2 overflow-hidden border border-ivory/15 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)]"
            >
              <img src={IMAGES.semifreddo2} alt="Torta gelato Imperiale nella scatola brandizzata" loading="lazy" className="h-full w-full object-cover" />
            </motion.div>
          </div>
          <div className="lg:col-span-7 lg:pl-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8 }}
              className="max-w-xl space-y-5 text-base leading-relaxed text-ivory/70 sm:text-lg"
            >
              {t.semifreddi.paras.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <p className="font-display text-xl italic text-ivory sm:text-2xl">{t.semifreddi.closing}</p>
            </motion.div>
            <div className="mt-10 space-y-1">
              {SEMIFREDDI_MENU.map((item, i) => (
                <motion.div
                  key={item.id}
                  data-testid={`semifreddo-item-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  className="flex items-center gap-4 border-b border-ivory/10 py-4"
                >
                  {item.image && (
                    <button
                      data-testid={`semifreddo-thumb-${i}`}
                      onClick={() => setSelected({ ...item, detail: t.semifreddi.menu[item.id] })}
                      className="shrink-0 cursor-pointer transition-transform duration-300 hover:scale-110"
                      aria-label={item.name}
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        loading="lazy"
                        className="h-16 w-16 rounded-full border border-ivory/20 object-cover shadow-[0_8px_24px_-8px_rgba(0,0,0,0.7)] sm:h-20 sm:w-20"
                      />
                    </button>
                  )}
                  <span className="font-display text-xl font-medium sm:text-2xl">{item.name}</span>
                  <span className="flex-1 border-b border-dotted border-ivory/25" />
                  <span className="max-w-[40%] text-right text-xs text-ivory/50 sm:text-sm">
                    {t.semifreddi.menu[item.id]}
                  </span>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10"
            >
              <span className="inline-block rounded-full border border-ivory/30 px-7 py-3.5 font-mono-alt text-xs font-bold uppercase tracking-[0.22em] text-ivory sm:text-sm">
                {t.semifreddi.note}
              </span>
            </motion.div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="semifreddo-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-night/90 p-5 backdrop-blur-md"
          >
            <motion.figure
              initial={{ scale: 0.85, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-2xl"
            >
              <img
                src={selected.image}
                alt={selected.name}
                className="arch-frame max-h-[75vh] w-auto border border-ivory/20 object-contain shadow-2xl"
              />
              <figcaption className="mt-5 text-center">
                <p className="font-display text-2xl font-semibold text-ivory sm:text-3xl">{selected.name}</p>
                <p className="mt-1 text-sm text-ivory/60">{selected.detail}</p>
              </figcaption>
              <button
                data-testid="semifreddo-lightbox-close"
                onClick={() => setSelected(null)}
                className="absolute -right-3 -top-3 flex h-11 w-11 items-center justify-center rounded-full bg-bordeaux text-ivory transition-transform hover:scale-110"
                aria-label="Chiudi foto"
              >
                <X className="h-5 w-5" />
              </button>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
